import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { MotionViewport, varFade } from 'src/components/animate';
import { m, useScroll } from 'framer-motion';
import Iconify from 'src/components/iconify';
import ScrollProgress from 'src/components/scroll-progress';
import { bgGradient } from 'src/theme/css';
// components
import RequestDemo from 'src/components/request-demo/request-demo';
// data
import { supplierBenefits, efficiencyMetrics, supplierTestimonials } from 'src/utils/data';

export default function ForSuppliersView() {
  const theme = useTheme();
  const { scrollYProgress } = useScroll();

  return (
    <>
      <ScrollProgress scrollYProgress={scrollYProgress} />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.success.main,
            0.08
          )} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
          pt: { xs: 10, md: 15 },
          pb: { xs: 10, md: 15 },
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, ${alpha(
                theme.palette.success.main,
                0.1
              )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
                theme.palette.info.main,
                0.08
              )} 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, ${alpha(
                theme.palette.warning.main,
                0.05
              )} 0%, transparent 50%)
            `,
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/assets/background/overlay_1.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1,
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '15%',
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: alpha(theme.palette.success.main, 0.1),
            zIndex: 0,
            animation: 'float 6s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '10%',
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: alpha(theme.palette.info.main, 0.1),
            zIndex: 0,
            animation: 'float 8s ease-in-out infinite reverse',
          }}
        />
        <Container
          maxWidth="lg"
          component={MotionViewport}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <m.div variants={varFade().inLeft}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 2,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  Grow Your
                  <Box component="span" sx={{ color: 'success.main', display: 'block' }}>
                    Supplier Business
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.primary',
                    mb: 3,
                    lineHeight: 1.6,
                  }}
                >
                  List your products once and reach thousands of restaurants. Automate operations,
                  increase visibility, and boost your sales with Cartify&apos;s supplier platform.
                </Typography>
                <RequestDemo
                  buttonText="Start Listing Your Products"
                  buttonVariant="contained"
                  buttonColor="success"
                  buttonSize="large"
                  startIcon={<Iconify icon="mdi:store" />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    bgcolor: 'success.main',
                    boxShadow: theme.customShadows.primary,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.customShadows.primary,
                      bgcolor: 'success.dark',
                    },
                  }}
                  defaultRole="Supplier"
                  customRoles={[
                    'Restaurant Owner',
                    'F&B Operator',
                    'Supplier',
                    'Distributor',
                    'Other',
                  ]}
                  title="Request a Demo"
                  description="Fill out the form below and we'll get back to you within 24 hours to schedule your personalized demo for our supplier platform."
                />
              </m.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <m.div variants={varFade().inRight}>
                <Box
                  component="img"
                  src="/assets/illustrations/illustration_dashboard.png"
                  alt="Supplier Management"
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    height: 'auto',
                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))',
                  }}
                />
              </m.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 10, md: 15 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: '700',
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                How Cartify Helps Suppliers
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Powerful tools and features designed specifically to help suppliers grow their
                business
              </Typography>
            </Box>
          </m.div>

          <Grid container spacing={4}>
            {supplierBenefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <m.div variants={varFade().inUp}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'background.paper',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.customShadows.z24,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette[benefit.colorKey].main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <Iconify
                          icon={benefit.icon}
                          width={40}
                          height={40}
                          sx={{ color: theme.palette[benefit.colorKey].main }}
                        />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 2,
                        }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {benefit.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </m.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Efficiency Metrics Section */}
      <Box sx={{ py: { xs: 10, md: 15 }, bgcolor: 'background.neutral' }}>
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: '700',
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Increase Sales & Efficiency
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Real results from suppliers who have transformed their business with Cartify
              </Typography>
            </Box>
          </m.div>

          <Grid container spacing={4}>
            {efficiencyMetrics.map((metric, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <m.div variants={varFade().inUp}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      textAlign: 'center',
                      bgcolor: 'background.paper',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.customShadows.z24,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <Iconify
                          icon={metric.icon}
                          width={40}
                          height={40}
                          sx={{ color: theme.palette.primary.main }}
                        />
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          color: 'primary.main',
                          mb: 1,
                        }}
                      >
                        {metric.value}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 2,
                        }}
                      >
                        {metric.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {metric.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </m.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 10, md: 15 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: '700',
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                What Suppliers Say
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Join hundreds of satisfied suppliers who have transformed their business with
                Cartify.
              </Typography>
            </Box>
          </m.div>

          <Grid container spacing={4}>
            {supplierTestimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <m.div variants={varFade().inUp}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.customShadows.z24,
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={3} alignItems="center">
                        <Avatar
                          src={testimonial.avatar}
                          sx={{
                            width: 80,
                            height: 80,
                            border: `4px solid ${theme.palette.success.main}`,
                            overflow: 'hidden',
                            '& img': {
                              objectFit: 'contain',
                              width: '85%',
                              height: '85%',
                              borderRadius: '50%',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Iconify
                              key={i}
                              icon="solar:star-bold-duotone"
                              sx={{
                                color: theme.palette.warning.main,
                                width: 20,
                                height: 20,
                                mx: 0.5,
                              }}
                            />
                          ))}
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            fontStyle: 'italic',
                          }}
                        >
                          &quot;{testimonial.content}&quot;
                        </Typography>
                        <Divider sx={{ width: '100%' }} />
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                            }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            {testimonial.role}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontStyle: 'italic',
                            }}
                          >
                            {testimonial.company}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </m.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          ...bgGradient({
            color: alpha(theme.palette.success.main, 0.1),
            imgUrl: '/assets/background/overlay_3.jpg',
          }),
          py: { xs: 10, md: 15 },
        }}
      >
        <Container maxWidth="md" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 3,
                }}
              >
                Ready to Grow Your Business?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                Join thousands of suppliers who have already increased their sales and efficiency
                with Cartify. Start listing your products today.
              </Typography>
              <RequestDemo
                buttonText="Start Listing Your Products"
                buttonVariant="contained"
                buttonColor="success"
                buttonSize="large"
                startIcon={<Iconify icon="mdi:store" />}
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  bgcolor: 'success.main',
                  boxShadow: theme.customShadows.primary,
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: theme.customShadows.primary,
                    bgcolor: 'success.dark',
                  },
                }}
                defaultRole="Supplier"
                customRoles={[
                  'Restaurant Owner',
                  'F&B Operator',
                  'Supplier',
                  'Distributor',
                  'Other',
                ]}
                title="Request a Demo"
                description="Fill out the form below and we'll get back to you within 24 hours to schedule your personalized demo for our supplier platform."
              />
            </Box>
          </m.div>
        </Container>
      </Box>
    </>
  );
}
